import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, Section } from "../../components/Common";
import { toast } from "sonner";
import { Save, Palette, Upload, Loader2 } from "lucide-react";

export default function AdminSettings() {
  const [settings, setSettings] = useState({ institute_name: "", primary_color: "#2563EB", logo_url: "", tagline: "" });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploadingLogo(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setSettings((s) => ({ ...s, logo_url: fullUrl }));
      toast.success("Logo uploaded — click Save to apply");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/settings");
      setSettings({
        institute_name: data.institute_name || "",
        primary_color: data.primary_color || "#2563EB",
        logo_url: data.logo_url || "",
        tagline: data.tagline || "",
      });
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", settings);
      const { refreshSettings } = await import("../../lib/settings");
      await refreshSettings();
      toast.success("Branding updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Institute branding" subtitle="White-label your dashboard with your name, logo and color." />

      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Settings">
          <form onSubmit={save} className="space-y-4" data-testid="settings-form">
            <div>
              <label className="text-sm font-semibold text-slate-700">Institute name</label>
              <input value={settings.institute_name} onChange={(e) => setSettings({ ...settings, institute_name: e.target.value })}
                data-testid="settings-name"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Tagline</label>
              <input value={settings.tagline} onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                data-testid="settings-tagline"
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Logo</label>
              <div className="mt-1.5 flex gap-2">
                <input value={settings.logo_url} onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                  data-testid="settings-logo"
                  placeholder="https://... or upload" className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none" />
                <label className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg bg-blue-50 text-blue-700 font-semibold text-sm cursor-pointer hover:bg-blue-100"
                  data-testid="settings-logo-upload-label">
                  {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploadingLogo ? "..." : "Upload"}
                  <input type="file" hidden accept="image/*" onChange={onLogoFile} data-testid="settings-logo-file" />
                </label>
              </div>
              {settings.logo_url && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={settings.logo_url} alt="preview" className="h-10 w-10 rounded-lg object-cover border border-slate-200" />
                  <button type="button" onClick={() => setSettings({ ...settings, logo_url: "" })}
                    data-testid="settings-logo-clear"
                    className="text-xs text-rose-600 hover:underline">Remove</button>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Primary color</label>
              <div className="mt-1.5 flex gap-2">
                <input type="color" value={settings.primary_color} onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  data-testid="settings-color" className="h-11 w-14 rounded-lg border border-slate-200 cursor-pointer" />
                <input value={settings.primary_color} onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none font-mono text-sm" />
              </div>
            </div>
            <button type="submit" disabled={saving} data-testid="settings-save"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60">
              <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save changes"}
            </button>
          </form>
        </Section>

        <Section title="Live preview">
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3" style={{ background: settings.primary_color }}>
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="logo" className="h-9 w-9 rounded-lg object-cover bg-white" />
              ) : (
                <div className="h-9 w-9 rounded-lg bg-white grid place-items-center font-heading font-black" style={{ color: settings.primary_color }}>
                  {(settings.institute_name || "Y")[0]}
                </div>
              )}
              <div className="text-white">
                <div className="font-heading font-black">{settings.institute_name || "Your institute"}</div>
                <div className="text-xs opacity-80 italic">"{settings.tagline || "Your tagline"}"</div>
              </div>
            </div>
            <div className="p-6 bg-white">
              <div className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: settings.primary_color }}>
                <Palette className="inline h-3 w-3 mr-1" /> Sample dashboard
              </div>
              <div className="text-2xl font-heading font-bold text-slate-900">Welcome back!</div>
              <button className="mt-3 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ background: settings.primary_color }}>
                Primary action
              </button>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500">Changes apply across all dashboards once saved.</p>
        </Section>
      </div>
    </div>
  );
}
